import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
	createRoutesFromChildren,
	matchRoutes,
	RouterProvider,
	useLocation,
	useNavigationType,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { ConfigProvider } from 'antd';
import { AuthProvider } from 'providers/keycloak/provider';

import { store } from 'app/appStore';
import { MainLayout } from 'app/layouts';
import { routerPublicApp } from 'app/router';
import { THEME_LIGHT } from 'shared/consts';
import { ELanguages } from 'shared/typings';

import 'shared/styles/index.scss';

import { sentryEnvironment } from '../../sentry-environment';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

!!sentryDsn &&
	Sentry.init({
		dsn: sentryDsn,
		integrations: [
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.replayIntegration(),
		],
		denyUrls: [
			// Chrome extensions
			/extensions\//i,
			/extension\//i,
			/^chrome:\/\//i,
			// Facebook flakiness
			/graph\.facebook\.com/i,
		],
		release: sentryEnvironment.release,
		tracesSampleRate: 1,
	});

const isPublicRoute = window.location.pathname.startsWith('/public');

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<Provider store={store}>
			<AuthProvider
				currentRouteRequiresAuth={!isPublicRoute}
				locale={window?.localStorage?.i18nextLng || ELanguages.EN}>
				<ConfigProvider theme={THEME_LIGHT}>
					{isPublicRoute ? <RouterProvider router={routerPublicApp} /> : <MainLayout />}
				</ConfigProvider>
			</AuthProvider>
		</Provider>
	</StrictMode>,
);
